import {KEY_POINT_TOKENS} from "../../../constants/tokens";
import {KEY_POINT_CONFIGS} from "../../../configs/tokens";
import {BOT_WEIGHTS} from "../botConfig";
import {keyToCoords} from "../../coords/keyToCoords";
import {RUINS_INCREASED_INCOME} from "../../../configs/income";
import {FINAL_ROUND} from "../../../configs/game";
import {
  checkIfRoundWithIncreasedRuinsIncome,
  getRuinsIncreasedIncomeStartRound,
} from "../../gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {getRoundLimit} from "../../checkIfFinalRound";

const KEY_POINT_TOKEN_VALUES = Object.values(KEY_POINT_TOKENS);

// За сколько раундов до окна удвоенного дохода руин начинаем их ценить по-новому
// (пристроиться кварталом надо заранее)
const RUINS_VALUE_LOOKAHEAD_ROUNDS = 2;

export const checkIfRuinsToken = (token) => {
  return token === KEY_POINT_TOKENS.RUINS || token === KEY_POINT_TOKENS.SUPER_RUINS;
};

/**
 * С какого раунда руины оцениваются по удвоенному доходу последних раундов.
 *
 * ruinsIncomeRoundLimit — лимит раундов, от которого партия считает окно
 * дохода руин (selectRuinsIncomeRoundLimit). null — партия без лимита, окна
 * нет (Infinity). Не передан — FINAL_ROUND: вызов без лимита партии оценивает
 * руины по-прежнему.
 */
export const getRuinsBoostStartRound = (ruinsIncomeRoundLimit = FINAL_ROUND) => {
  const increasedIncomeStartRound = getRuinsIncreasedIncomeStartRound(ruinsIncomeRoundLimit);
  return increasedIncomeStartRound === null
    ? Infinity
    : increasedIncomeStartRound - RUINS_VALUE_LOOKAHEAD_ROUNDS;
};

export const checkIfKeyPointToken = (token) => {
  return KEY_POINT_TOKEN_VALUES.includes(token);
};

/**
 * Коэффициент близости ячейки к центру поля: 1.0 на краю, до 1.5 в центре.
 * Зверолюди ценят ключевые точки центральной части поля выше.
 */
export const calcCenterProximityFactor = (hexKey, fieldRadius) => {
  if (!fieldRadius) {
    return 1;
  }
  const [q, r] = keyToCoords(hexKey);
  const distanceFromCenter = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;
  return 1 + 0.5 * Math.max(0, 1 - distanceFromCenter / fieldRadius);
};

/**
 * Ключевые точки, контролируемые ДРУГИМИ игроками (включая нейтральные города).
 * Используется для оценки блокировки дохода противника своим отрядом.
 */
export const getEnemyControlledKeyPoints = (citiesCache, currentPlayerColor) => {
  const enemyKeyPoints = new Map(); // hexKey -> token
  Object.entries(citiesCache || {}).forEach(([color, playerCities]) => {
    if (color === currentPlayerColor) {
      return;
    }
    playerCities.forEach((city) => {
      KEY_POINT_TOKEN_VALUES.forEach((token) => {
        (city[token] || []).forEach((hexKey) => enemyKeyPoints.set(hexKey, token));
      });
    });
  });
  return enemyKeyPoints;
};

/**
 * Цвет владельца каждой чужой ключевой точки (hexKey -> цвет игрока).
 * Нужен там, где важно НЕ только «точка чужая», но и чья именно — например
 * при давлении ботов на сильного игрока (см. src/configs/botPressure.js).
 */
export const getEnemyKeyPointOwnerColors = (citiesCache, currentPlayerColor) => {
  const ownerColors = new Map(); // hexKey -> color
  Object.entries(citiesCache || {}).forEach(([color, playerCities]) => {
    if (color === currentPlayerColor) {
      return;
    }
    playerCities.forEach((city) => {
      KEY_POINT_TOKEN_VALUES.forEach((token) => {
        (city[token] || []).forEach((hexKey) => ownerColors.set(hexKey, color));
      });
    });
  });
  return ownerColors;
};

/**
 * Оценка ценности ключевой точки в "очках за раунд".
 * Очки идут 1:1, монеты конвертируются 2:1 в конце игры.
 * С учетом раунда (если передан): в последних раундах доход руин удваивается
 * (Руины 4, Великие руины 8 очков за раунд) — ценим их заранее, чтобы успеть
 * пристроиться кварталами и отбить у нейтральных охранников.
 * ruinsIncomeRoundLimit — как в getRuinsBoostStartRound.
 */
export const getKeyPointValuePerRound = (token, round = null, ruinsIncomeRoundLimit = FINAL_ROUND) => {
  const config = KEY_POINT_CONFIGS.find((c) => c.id === token);
  if (!config) {
    return 0;
  }

  if (round && checkIfRuinsToken(token) && round >= getRuinsBoostStartRound(ruinsIncomeRoundLimit)) {
    return (config.incomeCoins || 0) * BOT_WEIGHTS.COINS_TO_POINTS + (RUINS_INCREASED_INCOME[token] || 0);
  }

  return (config.incomeCoins || 0) * BOT_WEIGHTS.COINS_TO_POINTS + (config.incomeWinPoints || 0);
};

/**
 * Последний раунд, до которого бот суммирует доход точки: лимит партии, а без
 * лимита (null) — условный FINAL_ROUND, как раньше.
 */
export const getKeyPointHorizonEndRound = (ruinsIncomeRoundLimit = FINAL_ROUND) => (
  getRoundLimit(ruinsIncomeRoundLimit) ?? FINAL_ROUND
);

/**
 * Волна 32, блок E: суммарная ценность ключевой точки с раунда fromRound по
 * последний раунд партии включительно (доход начисляется в конце каждого
 * раунда, включая текущий). Руины — реальный доход по раундам: удвоение только
 * в последних раундах партии (R11-13 при 13 раундах).
 * Монеты — с весом COINS_TO_POINTS, как в getKeyPointValuePerRound.
 * Бонус FANATICISM сюда не входит (прибавляется вызывающим, как раньше).
 * Использует E4 (calcE32CityFollowUpValue). Раунд 5: горизонт в оценке стройки
 * (E1, getKeyPointEffectiveValuePerRound) удален — A/B r4: −0.55 очка; стройка
 * считает точки по getKeyPointValuePerRound.
 * ruinsIncomeRoundLimit — как в getRuinsBoostStartRound.
 */
export const getKeyPointHorizonValue = (token, fromRound, ruinsIncomeRoundLimit = FINAL_ROUND) => {
  const config = KEY_POINT_CONFIGS.find((c) => c.id === token);
  if (!config || !Number.isFinite(fromRound)) {
    return 0;
  }
  const coinsValue = (config.incomeCoins || 0) * BOT_WEIGHTS.COINS_TO_POINTS;
  const horizonEndRound = getKeyPointHorizonEndRound(ruinsIncomeRoundLimit);
  let sum = 0;
  for (let r = Math.max(1, fromRound); r <= horizonEndRound; r += 1) {
    const winPoints = checkIfRuinsToken(token) && checkIfRoundWithIncreasedRuinsIncome(r, ruinsIncomeRoundLimit)
      ? (RUINS_INCREASED_INCOME[token] || 0)
      : (config.incomeWinPoints || 0);
    sum += coinsValue + winPoints;
  }
  return sum;
};

/**
 * Множество ключевых точек, уже контролируемых каким-либо городом (включая нейтральные).
 */
export const getControlledKeyPointHexKeys = (citiesCache) => {
  const controlled = new Set();
  if (!citiesCache) {
    return controlled;
  }

  Object.values(citiesCache).forEach((playerCities) => {
    playerCities.forEach((city) => {
      KEY_POINT_TOKEN_VALUES.forEach((token) => {
        const hexKeys = city[token];
        if (Array.isArray(hexKeys)) {
          hexKeys.forEach((hexKey) => controlled.add(hexKey));
        }
      });
    });
  });

  return controlled;
};
